import { useCallback, useEffect, useMemo } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import BigNumber from 'bignumber.js';

import { useGetMainTokenBalanceQuery } from 'modules/api/actions/getMainTokenBalance';
import { useGetMainTokenUSDRatioQuery } from 'modules/api/actions/getMainTokenUSDRatio';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import {
  DEFAULT_LONG_DECIMAL_PLACES,
  HUNDRED,
  MAX_DECIMAL_PLACES,
  ZERO,
} from 'modules/common/const';
import { useGetLiquidStakingAPY } from 'modules/liquidStaking/hooks/useGetLiquidStakingAPY';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

interface IUseStakeFormValues extends FieldValues {
  amount: string;
  receiveAmount: string;
}

interface IUseStakeFormResult {
  amount: BigNumber;
  incomeAmount: BigNumber;
  incomeAmountUSD?: BigNumber;
  apy: BigNumber;
  balance: BigNumber;
  receiveAmount: BigNumber;
  ratio: BigNumber;
  onSubmit: (handleSubmit: (amount: BigNumber) => void) => () => void;
  isSubmitDisabled: boolean;
  onMaxClick: () => void;
  control: Control<IUseStakeFormValues>;
  errors: FieldErrors<IUseStakeFormValues>;
}

export function useStakeForm(): IUseStakeFormResult {
  const { isConnected, connectionKey, cacheTime } = useConnection();
  const { data: balance = ZERO } = useGetMainTokenBalanceQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { ratio } = useStakeToken();
  const { data: mainUSDRatio } = useGetMainTokenUSDRatioQuery();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<IUseStakeFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
      receiveAmount: '',
    },
  });

  const amountInputValue = watch('amount');
  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const onSubmit = useCallback(
    (onSubmitCallback: (amount: BigNumber) => void) => {
      return handleSubmit(({ amount }) => {
        const convertedAmount = new BigNumber(amount);

        onSubmitCallback(convertedAmount);
      });
    },
    [handleSubmit],
  );

  const onMaxClick = useCallback(
    () =>
      setValue('amount', balance.decimalPlaces(MAX_DECIMAL_PLACES).toString(), {
        shouldValidate: true,
      }),
    [balance, setValue],
  );

  const isAmountError = !!errors.amount;
  const isSubmitDisabled = useMemo(
    () =>
      isAmountError ||
      convertedAmount.isZero() ||
      convertedAmount.isGreaterThan(balance),
    [balance, convertedAmount, isAmountError],
  );

  const { apy } = useGetLiquidStakingAPY();
  const incomeAmount = useMemo(
    () => convertedAmount.multipliedBy(apy.div(HUNDRED)),
    [convertedAmount, apy],
  );

  const incomeAmountUSD = useMemo(
    () => mainUSDRatio && incomeAmount.multipliedBy(mainUSDRatio),
    [mainUSDRatio, incomeAmount],
  );

  const receiveAmount = useMemo(
    () => convertedAmount.multipliedBy(ratio),
    [convertedAmount, ratio],
  );

  useEffect(() => {
    setValue(
      'receiveAmount',
      receiveAmount.decimalPlaces(DEFAULT_LONG_DECIMAL_PLACES).toString(),
    );
  }, [receiveAmount, setValue]);

  return {
    amount: convertedAmount,
    receiveAmount,
    incomeAmount,
    incomeAmountUSD,
    balance,
    ratio,
    apy,
    onSubmit,
    onMaxClick,
    errors,
    control,
    isSubmitDisabled,
  };
}
